<template lang="pug">
.home(v-click-outside="closeEvent")
  .field.has-addons.has-addons-centered
    .notification.is-danger.p(:style="`flex-basis: ${width}; max-width: ${width};`"  v-if="$store.state.error") {{ $store.state.error }}
      button.delete(@click.prevent="$store.state.error = null")
  .field.has-addons.has-addons-centered.search-container(
    :style="`max-width: ${width}`"
  )
    .control(:style="`flex-basis: ${width}; height: 3rem;`")
      input.input.location-search(
        @focus="textFocused = true",
        @input="searchLocation",
        v-model="searchText",
        :style="`max-width: ${width}`",
        :placeholder="'Enter your street number and street address of your house or complex'"
      )
      .placeholder Enter your street number and street address of your house or complex
        span(style="color: #01a5e8") *
    p.control
      button.button.search-button
        fa-icon(icon="search")
    .location-suggestions(
      :class="{ 'is-block': textFocused && searchText.length > 3 }"
    )
      .has-text-left(
        v-if="isEmpty(search)",
        style="padding: 2rem; flex: 1; justify-items: center"
      )
        p.has-text-red Nothing found for this address - Please try the following format:
        p House Number, Street Name, Street Suffix, City, State, Zip code, Country
      a.has-text-left.location-option(
        v-else,
        v-for="loc of search",
        :key="loc.id",
        @click.prevent="selectItem(loc)"
      )
        | {{ loc.description }}
      div(ref="attrib")
</template>

<script>
import _ from "lodash";
import brand from "@/brand";

export default {
  props: {
    width: {
      default: "920px",
    },
    value: {
      default: "",
    },
    limit: {
      default: null,
    },
  },
  data() {
    return {
      searchText: this.value,
      textFocused: false,
      search: undefined,
      window: {
        width: 0,
        height: 0,
      },
      autocomplete: undefined,
      places: undefined,
    };
  },
  created() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  async mounted() {
    await this.$gmapApiPromiseLazy();
    /* eslint-disable */
    this.autocomplete = new google.maps.places.AutocompleteService();
    this.places = new google.maps.places.PlacesService(this.$refs.attrib);
    /* eslint-enable */
    if (!_.isEmpty(this.searchText) && !this.search) {
      this.searchLocation();
    }
  },
  watch: {
    value(nVal) {
      this.searchText = nVal;
      if (!_.isEmpty(this.searchText) && !this.search) {
        this.searchLocation();
      }
    },
  },
  methods: {
    closeEvent: function () {
      this.textFocused = false;
    },
    handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    },
    searchLocation: _.debounce(
      async function searchLocation() {
        const upingtonCenter = {lat: -28.435064, lng: 21.233283 };
        // Create a bounding box with sides ~80km away from the center point
        const upingtonBounds = {
          north: upingtonCenter.lat + 0.2,
          south: upingtonCenter.lat - 0.2,
          east: upingtonCenter.lng + 0.2,
          west: upingtonCenter.lng - 0.2,
        };

        const capeTownCenter = {lat: -33.925278, lng: 18.423889};
        const capeTownBounds = {
          north: capeTownCenter.lat + 0.2,
          south: capeTownCenter.lat - 0.2,
          east: capeTownCenter.lng + 0.4,
          west: capeTownCenter.lng - 0.2,
        };

        const searchBounds = {
          "cybersmart": {},
          "ftta"      : {bounds: capeTownBounds},
          "gigalight" : {bounds: upingtonBounds}
        };

        if (this.searchText.length > 3) {
          let params = {
            ...searchBounds[brand.getBrand()],
            input: this.searchText,
            componentRestrictions: { country: "za" },
          };

          if (this.limit) params = { ...this.limit, ...params };
          this.autocomplete.getPlacePredictions(
            params,
            this.displaySuggestions
          );
        }
      },
      100,
      {
        trailing: true,
        leading: true,
      }
    ),
    isEmpty: _.isEmpty,
    displaySuggestions(prediction, status) {
      // eslint-disable-next-line
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        return;
      }

      this.$emit("newSearch");
      this.search = prediction;
    },
    selectItem(loc) {
      this.searchText = loc.description;
      this.$emit("selected", loc);
      this.textFocused = false;
    },
  },
};
</script>

<style lang="scss">
@import "../styles/variables";

.home {
  .search-container {
    display: inline-flex;
    width: 100%;
    position: relative;

    .location-suggestions {
      display: none;
      position: absolute;
      background-color: #fafafa;
      min-height: 5rem;
      width: 100%;
      top: 100%;
      z-index: 4;

      .location-option {
        margin: 1rem 0;
        padding: 0.25rem 2rem;
        display: flex;
        justify-items: center;
        transition: background-color 100ms, color 100ms;
        color: unset;

        &:hover {
          background-color: $primary-colour;
          color: #fff;
        }
      }
    }

    .location-search {
      border: none;
      border-radius: 0;
      z-index: 3 !important;
      background: transparent;
      box-shadow: -1px 1px 4px 1px rgba(80, 80, 80, 0.15);
      font-size: 1rem;
      height: 2.25em;
      line-height: 1.5;
      padding: 1.5rem 1rem;

      &:focus {
        box-shadow: -3px 5px 8px 1px rgba(80, 80, 80, 0.35);
      }

      &::placeholder {
        opacity: 0;
        color: $secondary-colour;
        display: block;
      }

      & + .placeholder {
        visibility: hidden;
        margin-top: -2.275rem;
        width: 100%;
        font-weight: normal;
        text-align: left;
        margin-left: 1rem;
        line-height: 1.7;
        font-size: 0.7rem;
        color: $secondary-colour;
      }

      &:placeholder-shown + .placeholder {
        visibility: visible;
      }
    }

    .search-button {
      background-color: $primary-colour;
      color: #fff;
      border: none;
      border-radius: 0;
      box-shadow: -3px 5px 8px 1px rgba(80, 80, 80, 0.35);
      padding: 1rem 1.5rem;
      z-index: 2 !important;
      height: 100%;
      width: 70px;

      &:focus {
        box-shadow: -3px 5px 8px 1px rgba(80, 80, 80, 0.35);
      }
    }
  }
}
</style>
